import { phantomWallet } from "@rainbow-me/rainbowkit/wallets";
import { createConnector } from "wagmi";
import { injected } from "wagmi/connectors";

import { impersonatesPhantom } from "@/lib/wallet/phantom-genuine";
import { isGenericEnabled } from "@/lib/wallet/solana/allowlist";

import type { Wallet, WalletDetailsParams } from "@rainbow-me/rainbowkit";

/**
 * Phantom's Ethereum side at the namespace it keeps for itself.
 *
 * `window.phantom.ethereum` rather than the `isPhantom` flag on
 * `window.ethereum`: the flag says only that Phantom is injected somewhere,
 * and the entry below is worth offering only where the EVM provider itself is
 * reachable. A Phantom that sets the flag and nothing else is still reached
 * through `browserWallet`, which is why `phantomCoversIt` asks this first.
 *
 * A property read and nothing more — deliberately. Every other question about
 * this provider is an RPC call, and an account Phantom holds from an imported
 * Solana key answers those with a blocking "Unsupported account" window.
 */
export function hasPhantomEthereum(): boolean {
  if (typeof window === "undefined") return false;
  const phantom: unknown = Reflect.get(window, "phantom");
  if (typeof phantom !== "object" || phantom === null) return false;
  const ethereum: unknown = Reflect.get(phantom, "ethereum");
  if (typeof ethereum !== "object" || ethereum === null) return false;
  // Exodus and Magic Eden can take this namespace for themselves; a wallet
  // passing for Phantom here is not Phantom's entry to offer. Only where the
  // generic Solana layer runs: a flag-unset build (production) is main's.
  return !isGenericEnabled() || !impersonatesPhantom(ethereum);
}

/**
 * The one record of a holder having chosen Phantom as their *EVM* wallet.
 *
 * Phantom is the only wallet the site asks holders to install for a different
 * chain, and one approval covers the whole extension: connecting it for Solana
 * leaves its Ethereum side answering `eth_accounts` for this origin as well.
 * Every automatic path wagmi has into a connector reads exactly that — it
 * never asks who chose what — so for Phantom alone "authorised" has to mean
 * something narrower than the wallet is willing to say. This key is that
 * narrower thing: it is written when the holder picks Phantom out of the
 * picker, and removed when they disconnect it.
 *
 * `localStorage`, because it has to outlast a reload the way wagmi's own
 * connection state does. Unreadable is read as "not chosen": the cost is a
 * holder reconnecting by hand, and the alternative is the wrong address on the
 * claim page.
 */
const PHANTOM_EVM_CHOSEN = "mudwater.phantom-evm.chosen";

function phantomEvmChosen(): boolean {
  try {
    return localStorage.getItem(PHANTOM_EVM_CHOSEN) === "1";
  } catch {
    return false;
  }
}

function rememberPhantomEvm(): void {
  try {
    localStorage.setItem(PHANTOM_EVM_CHOSEN, "1");
  } catch {
    // A blocked store costs the reconnect, never correctness: the connector
    // simply reports no provider on the next load.
  }
}

function forgetPhantomEvm(): void {
  try {
    localStorage.removeItem(PHANTOM_EVM_CHOSEN);
  } catch {
    // As above, in the direction that cannot do harm.
  }
}

/**
 * Phantom, offered as an ApeChain wallet but reachable only on purpose.
 *
 * Listing it at all was the thing `multiInjectedProviderDiscovery: false` had
 * to stop, and the flag is not what made that safe — the connector list is.
 * `reconnect` walks that list, `createConfig` calls `setup()` on everything in
 * it, and both treat "the provider reports an account" as "the holder chose
 * this wallet". For every other entry those are the same sentence. For Phantom
 * they are not, because the site tells holders to connect it for Solana, and
 * that single approval makes its EVM side answer too. Put the stock entry in
 * the list and the original bug comes back by name: disconnect MetaMask,
 * reload, and the claim page checks a Phantom address for BAYC it will never
 * hold.
 *
 * So the entry carries a connector with one extra rule — nothing automatic
 * counts as the holder's choice — and the rule is applied at each of those
 * three doors rather than at the provider:
 *
 * - `setup()` does nothing, so no EIP-1193 listener is attached and Phantom
 *   cannot announce a connection the holder never asked for. That listener is
 *   not hypothetical: wagmi attaches it precisely so a wallet can connect
 *   itself.
 * - `isAuthorized()` answers false without asking the wallet anything, which
 *   is what stops `reconnect` adopting it.
 * - `onConnect` and `onAccountsChanged` drop a connection the wallet offers,
 *   which is what stops the listener `disconnect()` re-attaches on its way out
 *   from handing Phantom the connection back on the next Solana approval.
 *
 * `getProvider` is deliberately *not* one of them, though it looks like the
 * single place that would cover all three. RainbowKit asks a connector for its
 * `getChainId()` before it connects it, and that call reaches the wallet the
 * same way — so a connector with no provider until it is connected can never
 * be connected at all, and the picker sits on "Opening Phantom…" for good.
 * Nothing is lost by leaving it open: resolving the provider is a property
 * read on `window.phantom.ethereum` and sends the wallet nothing. What must
 * not happen on a page load is a *request*, and `eth_accounts` is sent by
 * `isAuthorized`, which is gated.
 *
 * Not asking is worth as much as not connecting. A Phantom account imported
 * from a Solana private key cannot do EVM at all, and the first request to its
 * Ethereum provider raises a blocking "Unsupported account" window. Those are
 * the holders this site creates most of — told to install Phantom for the rip
 * flow — and a window they cannot act on, on a page they only loaded, is the
 * worst version of this bug rather than a smaller one. After they pick Phantom
 * out of the picker it is theirs to see.
 *
 * The overrides delegate with `this` as the receiver rather than calling the
 * wrapped connector directly, which is what keeps the rule in force inside
 * wagmi's own code: its methods reach the provider as `this.getProvider()` and
 * bind their listeners as `this.onConnect.bind(this)`, so with the wrapper as
 * receiver both resolve here. Calling `base.connect()` plainly would leave
 * every one of them bound to the connector underneath, and `disconnect()` in
 * particular re-attaches a `connect` listener on its way out — which would
 * hand Phantom the connection back on the next Solana approval, one disconnect
 * after the holder had chosen it.
 *
 * What is deliberately *not* gated is anything that ends a connection:
 * `onAccountsChanged([])` still means disconnect, whatever this key says. A
 * holder switching accounts in Phantom drops its Solana and EVM sides
 * together, and both halves of the app degrade to disconnected, which is the
 * safe outcome for a claim session keyed on two addresses.
 */
export function phantomEntry(): Wallet {
  return {
    ...phantomWallet(),
    installed: hasPhantomEthereum(),
    createConnector: (walletDetails: WalletDetailsParams) =>
      createConnector((config) => {
        const base = injected({ target: "phantom" })(config);
        return {
          ...base,
          ...walletDetails,
          async setup() {
            if (!phantomEvmChosen()) return;
            await base.setup?.call(this);
          },
          async isAuthorized() {
            if (!phantomEvmChosen()) return false;
            return base.isAuthorized.call(this);
          },
          async connect<withCapabilities extends boolean = false>(
            parameters?: Parameters<typeof base.connect<withCapabilities>>[0]
          ): ReturnType<typeof base.connect<withCapabilities>> {
            // Before the call, not after: `base.connect` attaches the
            // wallet's event listeners as it goes, and they read this key.
            // Recorded afterwards, an `accountsChanged` arriving mid-approval
            // would be dropped by the connector that had just asked for it.
            rememberPhantomEvm();
            try {
              return await base.connect.call<
                typeof base,
                [typeof parameters],
                ReturnType<typeof base.connect<withCapabilities>>
              >(this, parameters);
            } catch (error) {
              // A refusal in the wallet is not a choice. Reconnecting is
              // exempt: the holder chose this already, and a wallet that is
              // merely locked must not cost them that.
              if (!parameters?.isReconnecting) forgetPhantomEvm();
              throw error;
            }
          },
          async disconnect() {
            // Cleared last, and whatever the wallet made of the request: this
            // key is the holder's answer, not the wallet's, and leaving it
            // behind is what would let the next Solana approval reconnect it.
            try {
              await base.disconnect.call(this);
            } finally {
              forgetPhantomEvm();
            }
          },
          onConnect(connectInfo) {
            if (!phantomEvmChosen()) return;
            base.onConnect?.call(this, connectInfo);
          },
          onAccountsChanged(accounts) {
            if (accounts.length > 0 && !phantomEvmChosen()) return;
            base.onAccountsChanged.call(this, accounts);
          },
        };
      }),
  };
}
